// The Scout tab's Approve card (GRO-162 §2.2). One card for everything Scout is waiting on, one
// click for all of it. The words come from the dashboard and render as plain text; the page
// decides nothing about what a click may do — the server refuses view-as and a stale card.
//
// An `add_to_sequence` item carries a dropdown of the person's sequences (P4-02). The pick rides
// on the click; the server checks it against the dropdown it drew.
//
// Written with `createElement` rather than JSX, and imported with its extension, so
// `ApprovalCard.test.ts` renders it under `node --test` with no bundler.

import { createElement as h, useState } from 'react';
import type { ReactElement } from 'react';
import type {
  ApprovalCardData, ApprovalDecision, ApprovalItem, SequenceChoices, SequencePicker,
} from '../lib/approvalCard.ts';
import { approveLabel, everySequencePicked, initialSequenceChoices } from '../lib/approvalCard.ts';

function SequenceSelect({ requestId, picker, value, disabled, onPick }: {
  requestId: string;
  picker: SequencePicker;
  value: string;
  disabled: boolean;
  onPick: (requestId: string, sequenceId: string) => void;
}): ReactElement {
  const selectId = `sequence-${requestId}`;
  return h('div', { className: 'approvalsequence' },
    h('label', { htmlFor: selectId }, 'Sequence'),
    h('select', {
      id: selectId, value, disabled,
      onChange: (event: { target: { value: string } }) => onPick(requestId, event.target.value),
    },
      value ? null : h('option', { value: '' }, 'Choose a sequence'),
      ...picker.options.map(option => h('option', { key: option.id, value: option.id },
        option.status === 'draft' ? `${option.name} (draft)` : option.name)),
    ),
    picker.truncated ? h('p', { className: 'approvalnote' }, 'Showing your newest sequences only.') : null,
  );
}

function ApprovalItemBlock({ item, choice, disabled, onPick }: {
  item: ApprovalItem;
  choice: string;
  disabled: boolean;
  onPick: (requestId: string, sequenceId: string) => void;
}): ReactElement {
  return h('div', { className: 'approvalitem' },
    h('p', { className: 'approvaltitle' }, item.title),
    item.lines.length > 0 ? h('ul', null, ...item.lines.map((line, index) => h('li', { key: index }, line))) : null,
    item.sequence_picker
      ? h(SequenceSelect, { requestId: item.request_id, picker: item.sequence_picker, value: choice, disabled, onPick })
      : null,
    item.note ? h('p', { className: 'approvalnote' }, item.note) : null,
  );
}

type DecideHandler = (decision: ApprovalDecision, sequenceChoices: SequenceChoices) => void;

export function ApprovalCard({ card, blockedReason, onDecide }: {
  card: ApprovalCardData;
  blockedReason: string | null;
  onDecide: DecideHandler;
}): ReactElement {
  // The picks belong to this card: a redrawn card starts again from its own preselections.
  const cardKey = card.request_ids.join(',');
  const [picked, setPicked] = useState<{ cardKey: string; choices: SequenceChoices } | null>(null);
  const choices = picked && picked.cardKey === cardKey ? picked.choices : initialSequenceChoices(card);
  function pick(requestId: string, sequenceId: string) {
    setPicked({ cardKey, choices: { ...choices, [requestId]: sequenceId } });
  }
  return h(ApprovalCardView, { card, blockedReason, choices, onPick: pick, onDecide });
}

/** The card as drawn for a given set of picks. No state, so a test can press its buttons. */
export function ApprovalCardView({ card, blockedReason, choices, onPick, onDecide }: {
  card: ApprovalCardData;
  blockedReason: string | null;
  choices: SequenceChoices;
  onPick: (requestId: string, sequenceId: string) => void;
  onDecide: DecideHandler;
}): ReactElement {
  const disabled = blockedReason !== null;
  const approveDisabled = disabled || !everySequencePicked(card, choices);
  return h('section', { className: 'approvalcard', 'aria-label': 'Waiting for your approval' },
    h('p', { className: 'approvalcardlabel' }, 'Waiting for your approval'),
    ...card.items.map(item => h(ApprovalItemBlock, {
      key: item.request_id, item, choice: choices[item.request_id] || '', disabled, onPick,
    })),
    h('div', { className: 'approvalactions' },
      // No Approve when the card cannot show what it would write (review round 3): only Cancel.
      card.approvable
        ? h('button', {
          type: 'button', className: 'btn primary', disabled: approveDisabled, onClick: () => onDecide('approve', choices),
        }, approveLabel(card))
        : null,
      h('button', { type: 'button', className: 'btn', disabled, onClick: () => onDecide('cancel', {}) }, 'Cancel'),
      blockedReason ? h('span', { className: 'approvalblocked', role: 'status' }, blockedReason) : null,
    ),
  );
}
